import React, { type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ShortcutIcon from '@atlaskit/icon/glyph/shortcut';
import { token } from '@atlaskit/tokens';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import messages from './messages';

const GLOBAL_SETTINGS_LINK = '/secure/admin/SDConfiguration.jspa';
const GLOBAL_CUSTOMER_ACCESS_SETTINGS_LINK = '/jira/settings/products/servicedesk/customer-access';

// visible for testing
export const tenantUrl = () => ({
	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	tenantUrl: window.location.origin,
});

export const WhoCanAccessTip = () => {
	const { formatMessage } = useIntl();

	return <>{formatMessage(messages.whoCanAccessChannels)}</>;
};

export const WhoCanAccessDescription = ({
	portalLink,
}: {
	projectName: string;
	portalLink: string;
	serviceDeskEmail: string;
}) => {
	const { formatMessage } = useIntl();

	return (
		<>
			{formatMessage(messages.channelsAccessDescription, {
				link: (...chunks: ReactNode[]) => (
					<Button
						iconAfter={
							<ShortcutIcon size="small" label={formatMessage(messages.shortcutIconLabel)} />
						}
						spacing="none"
						appearance="link"
						href={portalLink}
						target="_blank"
						onClick={(event, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'clickOnPortalButton');
						}}
					>
						<LinkTextWrapper>{chunks}</LinkTextWrapper>
					</Button>
				),
			})}
		</>
	);
};

export const OpenAccessTip = () => {
	const { formatMessage } = useIntl();

	return (
		<>
			{formatMessage(messages.channelsAccessSiteAdminHint, {
				link: (...chunks: ReactNode[]) => (
					<Button
						href={GLOBAL_CUSTOMER_ACCESS_SETTINGS_LINK}
						iconAfter={
							<ShortcutIcon size="small" label={formatMessage(messages.shortcutIconLabel)} />
						}
						appearance="link"
						spacing="none"
						target="_blank"
					>
						<LinkTextWrapper>{chunks}</LinkTextWrapper>
					</Button>
				),
				br: () => <br />,
			})}
		</>
	);
};

export const CustomerOrgSharingTip = () => {
	const { formatMessage } = useIntl();

	return (
		<FormattedMessage
			{...messages.organisationsSharingTip}
			values={{
				globalSettings: (
					<Button
						spacing="none"
						appearance="link"
						href={GLOBAL_SETTINGS_LINK}
						target="_blank"
						onClick={(event, analyticsEvent) => {
							fireUIAnalytics(analyticsEvent, 'clickOnCustomerOrgsSharingTip');
						}}
					>
						<FormattedMessage
							{...messages.globalSettingsLink}
							values={{
								shortcutIcon: (
									<ShortcutIcon size="small" label={formatMessage(messages.shortcutIconLabel)} />
								),
							}}
						/>
					</Button>
				),
			}}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LinkTextWrapper = styled.span({
	paddingRight: token('space.050', '4px'),
});
